import { ethers } from 'ethers'
import { CONTRACT_CONFIG, STORM_SIMPLE_ABI } from '../config/contract.js'

const WALLET_TYPES = {
  METAMASK: 'metamask',
  TRUST_WALLET: 'trustwallet',
  COINBASE: 'coinbase',
  OKX: 'okx',
  BINANCE: 'binance',
  WALLET_CONNECT: 'walletconnect',
  INJECTED: 'injected'
}

const WALLET_INFO = {
  [WALLET_TYPES.METAMASK]: {
    name: 'MetaMask',
    icon: '🦊',
    downloadUrl: 'https://metamask.io/download/',
    providerKey: 'ethereum',
    detect: () => typeof window !== 'undefined' && window.ethereum?.isMetaMask
  },
  [WALLET_TYPES.TRUST_WALLET]: {
    name: 'Trust Wallet',
    icon: '🔷',
    downloadUrl: 'https://trustwallet.com/download',
    providerKey: 'trustwallet',
    detect: () => typeof window !== 'undefined' && (window.trustwallet || window.ethereum?.isTrust)
  },
  [WALLET_TYPES.COINBASE]: {
    name: 'Coinbase Wallet',
    icon: '🔵',
    downloadUrl: 'https://www.coinbase.com/wallet',
    providerKey: 'coinbaseWalletExtension',
    detect: () => typeof window !== 'undefined' && (window.coinbaseWalletExtension || window.ethereum?.isCoinbaseWallet)
  },
  [WALLET_TYPES.OKX]: {
    name: 'OKX Wallet',
    icon: '⚫',
    downloadUrl: 'https://www.okx.com/web3',
    providerKey: 'okxwallet',
    detect: () => typeof window !== 'undefined' && window.okxwallet
  },
  [WALLET_TYPES.BINANCE]: {
    name: 'Binance Wallet',
    icon: '🟨',
    downloadUrl: 'https://www.binance.com/en/wallet',
    providerKey: 'BinanceChain',
    detect: () => typeof window !== 'undefined' && (window.BinanceChain || window.ethereum?.isBinance)
  },
  [WALLET_TYPES.INJECTED]: {
    name: '浏览器钱包',
    icon: '🔐',
    downloadUrl: null,
    providerKey: 'ethereum',
    detect: () => typeof window !== 'undefined' && window.ethereum
  }
}

class WalletService {
  constructor() {
    this.provider = null
    this.signer = null
    this.contract = null
    this.isConnected = false
    this.userAddress = null
    this.currentWalletType = null
    this.listeners = {}
  }

  getAvailableWallets() {
    const available = []
    for (const [type, info] of Object.entries(WALLET_INFO)) {
      if (info.detect()) {
        available.push({ type, ...info })
      }
    }
    
    if (available.length === 0) {
      available.push({
        type: WALLET_TYPES.METAMASK,
        ...WALLET_INFO[WALLET_TYPES.METAMASK],
        notInstalled: true
      })
    }
    
    return available
  }

  getWalletProvider(walletType) {
    const walletInfo = WALLET_INFO[walletType]
    if (!walletInfo) return null

    if (walletType === WALLET_TYPES.TRUST_WALLET) {
      return window.trustwallet || window.ethereum
    }
    if (walletType === WALLET_TYPES.OKX) {
      return window.okxwallet
    }
    if (walletType === WALLET_TYPES.BINANCE) {
      return window.BinanceChain || window.ethereum
    }
    if (walletType === WALLET_TYPES.COINBASE) {
      return window.coinbaseWalletExtension || window.ethereum
    }
    
    return window[walletInfo.providerKey] || window.ethereum
  }

  async initialize() {
    try {
      this.provider = new ethers.JsonRpcProvider(CONTRACT_CONFIG.network.rpcUrls[0])
      this.contract = new ethers.Contract(
        CONTRACT_CONFIG.address,
        STORM_SIMPLE_ABI,
        this.provider
      )
      console.log('钱包服务初始化（只读模式）')
      return true
    } catch (error) {
      console.error('钱包服务初始化失败:', error)
      return false
    }
  }

  async connectWallet(walletType = WALLET_TYPES.INJECTED) {
    try {
      const walletInfo = WALLET_INFO[walletType]
      if (!walletInfo) {
        throw new Error('不支持的钱包类型')
      }

      const rawProvider = this.getWalletProvider(walletType)
      
      if (!rawProvider) {
        if (walletInfo.downloadUrl) {
          window.open(walletInfo.downloadUrl, '_blank')
          throw new Error(`请先安装 ${walletInfo.name}`)
        }
        throw new Error('未检测到钱包，请安装钱包后重试')
      }

      this.provider = new ethers.BrowserProvider(rawProvider)
      
      const accounts = await this.provider.send('eth_requestAccounts', [])
      
      if (accounts.length === 0) {
        throw new Error('没有选择账户')
      }
      
      this.signer = await this.provider.getSigner()
      this.userAddress = accounts[0]
      this.isConnected = true
      this.currentWalletType = walletType
      
      this.contract = new ethers.Contract(
        CONTRACT_CONFIG.address,
        STORM_SIMPLE_ABI,
        this.signer
      )
      
      this.setupProviderListeners(rawProvider)
      
      console.log(`钱包连接成功 (${walletInfo.name}):`, this.userAddress)
      this.emit('connect', { address: this.userAddress, walletType })
      return true
    } catch (error) {
      console.error('连接钱包失败:', error)
      this.isConnected = false
      this.userAddress = null
      this.signer = null
      this.currentWalletType = null
      
      await this.initialize()
      
      throw error
    }
  }

  setupProviderListeners(provider) {
    if (provider.on) {
      provider.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          this.disconnect()
        } else {
          this.userAddress = accounts[0]
          this.emit('accountsChanged', accounts[0])
        }
      })

      provider.on('chainChanged', (chainId) => {
        this.emit('chainChanged', chainId)
      })
    }
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(callback)
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data))
    }
  }

  disconnect() {
    this.isConnected = false
    this.userAddress = null
    this.signer = null
    this.currentWalletType = null
    this.listeners = {}
    this.contract = null
    this.initialize()
    this.emit('disconnect')
  }

  async getContractInfo() {
    try {
      const [name, symbol, decimals, totalSupply, userCount] = await Promise.all([
        this.contract.name(),
        this.contract.symbol(),
        this.contract.decimals(),
        this.contract.totalSupply(),
        this.contract.userCount()
      ])

      return {
        name,
        symbol,
        decimals: Number(decimals),
        totalSupply: ethers.formatUnits(totalSupply, decimals),
        userCount: Number(userCount)
      }
    } catch (error) {
      console.error('获取合约信息失败:', error)
      throw error
    }
  }

  async getUserBalance(address = this.userAddress) {
    try {
      if (!this.contract || !address) {
        return '0'
      }
      const balance = await this.contract.balanceOf(address)
      const decimals = await this.contract.decimals()
      return ethers.formatUnits(balance, decimals)
    } catch (error) {
      console.error('获取用户余额失败:', error)
      return '0'
    }
  }

  async getBalance(address = this.userAddress) {
    try {
      if (!this.provider || !address) {
        return '0'
      }
      const balance = await this.provider.getBalance(address)
      return ethers.formatEther(balance)
    } catch (error) {
      console.error('获取BNB余额失败:', error)
      return '0'
    }
  }

  async hasUserClaimed(address = this.userAddress) {
    try {
      return await this.contract.hasClaimed(address)
    } catch (error) {
      console.error('检查用户参与状态失败:', error)
      throw error
    }
  }

  async explore(referrer = '0x0000000000000000000000000000000000000000') {
    try {
      if (!this.isConnected) {
        throw new Error('请先连接钱包')
      }

      const explorationFee = await this.contract.EXPLORATION_FEE()
      
      const tx = await this.contract.explore(referrer, {
        value: explorationFee
      })

      console.log('探索交易已提交:', tx.hash)
      
      const receipt = await tx.wait()
      console.log('探索交易已确认:', receipt)

      return {
        success: true,
        transactionHash: tx.hash,
        receipt
      }
    } catch (error) {
      console.error('探索挖矿失败:', error)
      throw error
    }
  }

  async getExplorationInfo() {
    try {
      const [
        explorationFee,
        baseRewardEarly,
        baseRewardLate,
        maxEarlyUsers,
        referralRate,
        userCount
      ] = await Promise.all([
        this.contract.EXPLORATION_FEE(),
        this.contract.BASE_REWARD_EARLY(),
        this.contract.BASE_REWARD_LATE(),
        this.contract.MAX_EARLY_USERS(),
        this.contract.REFERRAL_RATE(),
        this.contract.userCount()
      ])

      const decimals = await this.contract.decimals()
      
      return {
        explorationFee: ethers.formatEther(explorationFee),
        baseRewardEarly: ethers.formatUnits(baseRewardEarly, decimals),
        baseRewardLate: ethers.formatUnits(baseRewardLate, decimals),
        maxEarlyUsers: Number(maxEarlyUsers),
        referralRate: Number(referralRate),
        userCount: Number(userCount),
        isEarlyUser: Number(userCount) < Number(maxEarlyUsers)
      }
    } catch (error) {
      console.error('获取探索信息失败:', error)
      throw error
    }
  }

  async transfer(to, amount) {
    try {
      if (!this.isConnected) {
        throw new Error('请先连接钱包')
      }

      const decimals = await this.contract.decimals()
      const amountInWei = ethers.parseUnits(amount, decimals)
      
      const tx = await this.contract.transfer(to, amountInWei)
      const receipt = await tx.wait()

      return {
        success: true,
        transactionHash: tx.hash,
        receipt
      }
    } catch (error) {
      console.error('转账失败:', error)
      throw error
    }
  }
}

export const walletService = new WalletService()
export { WALLET_TYPES, WALLET_INFO }
export default walletService
